import {
  createSlice,
  createAsyncThunk,
  createSelector,
  PayloadAction,
} from "@reduxjs/toolkit";
import { lensProp, view, omit } from "ramda";

import { RootState } from "../../store/rootReducer";

import { Image, Images } from "./types";
import { compressFile } from "./utilities";

type ImagesState = Record<string, Images>;

const initialState: ImagesState = {};

const setImage = createAsyncThunk<
  { section: string; src: string; type: string },
  { section: string; src: File; type: string },
  { rejectValue: { section: string; type: string } }
>(
  "images/setAndCompressImage",
  async ({ section, src, type }, { rejectWithValue }) => {
    try {
      let image: string = URL.createObjectURL(src); /* LPPLWEBMOB-454 */
      if (!src.name.includes(".pdf")) image = await compressFile(src);

      return {
        section,
        src: image,
        type,
      };
    } catch (error) {
      return rejectWithValue({ section, type });
    }
  }
);

const imagesSlice = createSlice({
  name: "images",
  initialState,
  extraReducers: (builder) => {
    builder
      .addCase(
        setImage.pending,
        (
          state,
          {
            meta: {
              arg: { section, type },
            },
          }
        ) => {
          if (state[section] === undefined) {
            state[section] = {};
          }

          state[section][type] = {
            isDuringCompression: true,
            isCorrupted: false,
            src: "",
          };
        }
      )
      .addCase(
        setImage.fulfilled,
        (state, { payload: { section, src, type } }) => {
          state[section][type] = {
            isDuringCompression: false,
            isCorrupted: false,
            src,
          };
        }
      )
      .addCase(setImage.rejected, (state, { payload }) => {
        if (payload) {
          state[payload.section][payload.type] = {
            isDuringCompression: false,
            isCorrupted: true,
            src: "",
          };
        }
      });
  },
  reducers: {
    removeImage: (
      state,
      action: PayloadAction<{ section: string; type: string }>
    ) => {
      delete state[action.payload.section][action.payload.type];
    },
    removeAllImages: () => initialState,
  },
});

export { setImage };
export const { removeImage, removeAllImages } = imagesSlice.actions;

type selectorType = Partial<string | Array<string>>;
export const selectImages = (state: RootState): ImagesState => state.images;
function createParameterSelector(
  selector: (params: { [key: string]: selectorType }) => selectorType
) {
  return (_: RootState, params: { [key: string]: selectorType }) =>
    selector(params);
}
const selectImageType = createParameterSelector((params) => params.type);
const selectIgnoredKeys = createParameterSelector(
  (params) => params.ignoredKeys
);
const selectImageSection = createParameterSelector((params) => params.section);

export const selectImageByType = createSelector(
  selectImages,
  selectImageSection,
  selectImageType,
  (images, section, type) =>
    (view(
      lensProp(type as string),
      view(lensProp(section as string), images)
    ) as Image) || {
      isDuringCompression: false,
      isCorrupted: false,
      src: "",
    }
);

export const selectMultiImages = createSelector(
  selectImages,
  selectImageSection,
  selectIgnoredKeys,
  (images, section, ignoredKeys = []) =>
    (omit(
      ignoredKeys as string[],
      view(lensProp(section as string), images)
    ) as Images) || {}
);

export const { reducer } = imagesSlice;
